// src/pages/ClientDashboard.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  CircularProgress,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Chip,
  Stack,
  Divider,
} from "@mui/material";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import BuildIcon from "@mui/icons-material/Build";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import HandymanRoundedIcon from "@mui/icons-material/HandymanRounded";
import QueryBuilderRoundedIcon from "@mui/icons-material/QueryBuilderRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import PageLayout from "../components/PageLayout";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../contexts/firebase/config";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  limit,
} from "firebase/firestore";
import { Link as RouterLink, useNavigate } from "react-router-dom";

export default function ClientDashboard() {
  const { currentUser, userType } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    emAnalise: 0,
    emReparacao: 0,
    concluidos: 0,
  });
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    if (currentUser && userType === "cliente") {
      navigate("/cliente", { replace: true });
    }
  }, [currentUser, userType, navigate]);

  useEffect(() => {
    if (!currentUser) return;

    const baseQ = query(
      collection(db, "reservas"),
      where("clienteId", "==", currentUser.uid)
    );

    const unsubAll = onSnapshot(baseQ, (snap) => {
      const total = snap.size;
      let emAnalise = 0,
        emReparacao = 0,
        concluidos = 0;

      snap.forEach((d) => {
        const st = (d.data().status || "").toLowerCase();
        if (st.includes("análise") || st.includes("analise")) emAnalise++;
        else if (st.includes("repar")) emReparacao++;
        else if (st.includes("conclu")) concluidos++;
      });

      setStats({ total, emAnalise, emReparacao, concluidos });
      setLoading(false);
    });

    const unsubRecent = onSnapshot(
      query(baseQ, orderBy("createdAt", "desc"), limit(5)),
      (snap) => {
        setRecent(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      }
    );

    return () => {
      unsubAll();
      unsubRecent();
    };
  }, [currentUser]);

  const cards = [
    {
      label: "Minhas Reservas",
      value: stats.total,
      icon: <EventAvailableIcon sx={{ fontSize: 28, color: "#2563eb" }} />,
      to: "/reservas",
      tint: "rgba(37,99,235,0.10)",
    },
    {
      label: "Em Análise",
      value: stats.emAnalise,
      icon: <QueryBuilderRoundedIcon sx={{ fontSize: 28, color: "#d97706" }} />,
      tint: "rgba(245,158,11,0.12)",
    },
    {
      label: "Em Reparação",
      value: stats.emReparacao,
      icon: <HandymanRoundedIcon sx={{ fontSize: 28, color: "#7c3aed" }} />,
      tint: "rgba(124,58,237,0.10)",
    },
    {
      label: "Concluídos",
      value: stats.concluidos,
      icon: <TaskAltRoundedIcon sx={{ fontSize: 28, color: "#059669" }} />,
      tint: "rgba(16,185,129,0.10)",
    },
  ];

  const getStatusChip = (status) => {
    const value = status || "—";
    const normalized = value.toLowerCase();

    if (normalized.includes("análise") || normalized.includes("analise")) {
      return (
        <Chip
          label={value}
          size="small"
          sx={{
            fontWeight: 800,
            borderRadius: "999px",
            bgcolor: "rgba(245,158,11,0.14)",
            color: "#92400e",
            border: "1px solid rgba(245,158,11,0.20)",
          }}
        />
      );
    }

    if (normalized.includes("repar")) {
      return (
        <Chip
          label={value}
          size="small"
          sx={{
            fontWeight: 800,
            borderRadius: "999px",
            bgcolor: "rgba(124,58,237,0.12)",
            color: "#6d28d9",
            border: "1px solid rgba(124,58,237,0.16)",
          }}
        />
      );
    }

    if (normalized.includes("conclu")) {
      return (
        <Chip
          label={value}
          size="small"
          sx={{
            fontWeight: 800,
            borderRadius: "999px",
            bgcolor: "rgba(16,185,129,0.12)",
            color: "#065f46",
            border: "1px solid rgba(16,185,129,0.18)",
          }}
        />
      );
    }

    return (
      <Chip
        label={value}
        size="small"
        sx={{
          fontWeight: 800,
          borderRadius: "999px",
          bgcolor: "rgba(15,23,42,0.05)",
          border: "1px solid rgba(15,23,42,0.08)",
        }}
      />
    );
  };

  return (
    <PageLayout>
      <Box
        sx={{
          minHeight: "100vh",
          width: "100%",
          background: (theme) =>
            `radial-gradient(circle at top left, ${theme.palette.primary.main}18 0%, transparent 30%),
             radial-gradient(circle at top right, ${theme.palette.secondary.main}14 0%, transparent 24%),
             linear-gradient(180deg, #f8fafc 0%, #eef2ff 42%, #ffffff 100%)`,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "100%",
            mx: "auto",
            px: { xs: 2, sm: 3, md: 4, lg: 5 },
            py: { xs: 3, md: 4 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              position: "relative",
              overflow: "hidden",
              mb: 4,
              p: { xs: 3, md: 4.5 },
              borderRadius: "32px",
              border: "1px solid",
              borderColor: "rgba(255,255,255,0.65)",
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.72) 100%)",
              backdropFilter: "blur(14px)",
              boxShadow: "0 24px 80px rgba(15, 23, 42, 0.08)",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -60,
                right: -60,
                width: 220,
                height: 220,
                borderRadius: "50%",
                background: "rgba(99,102,241,0.14)",
                filter: "blur(40px)",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: -50,
                left: "28%",
                width: 180,
                height: 180,
                borderRadius: "50%",
                background: "rgba(168,85,247,0.10)",
                filter: "blur(42px)",
              }}
            />

            <Grid
              container
              spacing={4}
              alignItems="center"
              sx={{ position: "relative", zIndex: 1 }}
            >
              <Grid item xs={12} lg={7}>
                <Stack spacing={2.4}>
                  <Stack direction="row" spacing={1.2} alignItems="center">
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2.5,
                        display: "grid",
                        placeItems: "center",
                        background:
                          "linear-gradient(135deg, #4f46e5 0%, #2563eb 100%)",
                        color: "common.white",
                        boxShadow: "0 10px 24px rgba(79,70,229,0.24)",
                      }}
                    >
                      <DashboardRoundedIcon fontSize="small" />
                    </Box>

                    <Chip
                      label="Área do Cliente"
                      sx={{
                        width: "fit-content",
                        fontWeight: 800,
                        color: "#4338ca",
                        bgcolor: "rgba(99,102,241,0.08)",
                        borderRadius: "999px",
                        border: "1px solid rgba(99,102,241,0.12)",
                      }}
                    />
                  </Stack>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 900,
                        letterSpacing: "-0.05em",
                        fontSize: { xs: "2rem", sm: "2.4rem", md: "3rem" },
                        lineHeight: 1.03,
                        color: "#0f172a",
                      }}
                    >
                      👋 Olá,{" "}
                      {currentUser?.displayName ||
                        currentUser?.email?.split("@")[0]}
                    </Typography>

                    <Typography
                      sx={{
                        mt: 1.5,
                        maxWidth: 720,
                        color: "text.secondary",
                        fontSize: { xs: "0.98rem", md: "1.08rem" },
                        lineHeight: 1.75,
                      }}
                    >
                      Acompanha as tuas reparações, consulta o estado das
                      reservas e agenda novos serviços numa dashboard moderna,
                      clara e pensada para uma experiência mais fluida.
                    </Typography>
                  </Box>

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                  >
                  </Stack>
                </Stack>
              </Grid>

              <Grid item xs={12} lg={5}>
                <Grid container spacing={2}>
                  {cards.map(({ label, value, icon, to, tint }) => {
                    const content = (
                      <Card
                        elevation={0}
                        sx={{
                          height: "100%",
                          borderRadius: "24px",
                          border: "1px solid",
                          borderColor: "rgba(15,23,42,0.06)",
                          background: "rgba(255,255,255,0.82)",
                          boxShadow: "0 12px 34px rgba(15,23,42,0.06)",
                          transition: "all .25s ease",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: "0 20px 44px rgba(15,23,42,0.10)",
                          },
                        }}
                      >
                        <CardContent sx={{ p: 2.5 }}>
                          <Stack
                            direction="row"
                            alignItems="flex-start"
                            justifyContent="space-between"
                            spacing={2}
                          >
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "text.secondary",
                                  fontWeight: 700,
                                }}
                              >
                                {label}
                              </Typography>
                              <Typography
                                sx={{
                                  mt: 1,
                                  fontSize: "2rem",
                                  fontWeight: 900,
                                  lineHeight: 1,
                                  letterSpacing: "-0.04em",
                                  color: "#0f172a",
                                }}
                              >
                                {value}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                width: 54,
                                height: 54,
                                borderRadius: "18px",
                                display: "grid",
                                placeItems: "center",
                                bgcolor: tint,
                                border: "1px solid rgba(15,23,42,0.05)",
                              }}
                            >
                              {icon}
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    );

                    return (
                      <Grid item xs={12} sm={6} key={label}>
                        {to ? (
                          <CardActionArea
                            component={RouterLink}
                            to={to}
                            sx={{ borderRadius: "24px" }}
                          >
                            {content}
                          </CardActionArea>
                        ) : (
                          content
                        )}
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>
            </Grid>
          </Paper>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12} xl={8}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2.5, md: 3 },
                    borderRadius: "28px",
                    border: "1px solid rgba(15,23,42,0.06)",
                    background: "rgba(255,255,255,0.78)",
                    backdropFilter: "blur(12px)",
                    boxShadow: "0 24px 60px rgba(15,23,42,0.06)",
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    spacing={2}
                    sx={{ mb: 2.5 }}
                  >
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 900 }}>
                        📋 Últimas Reservas
                      </Typography>
                      <Typography color="text.secondary" sx={{ mt: 0.6 }}>
                        Consulta rapidamente os teus pedidos mais recentes.
                      </Typography>
                    </Box>

                    <Chip
                      icon={<ReceiptLongRoundedIcon />}
                      label={`${recent.length} registo(s)`}
                      sx={{
                        borderRadius: "999px",
                        fontWeight: 800,
                        bgcolor: "rgba(15,23,42,0.04)",
                      }}
                    />
                  </Stack>

                  <TableContainer
                    sx={{
                      borderRadius: "22px",
                      border: "1px solid rgba(15,23,42,0.06)",
                      overflow: "hidden",
                    }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow
                          sx={{
                            "& th": {
                              bgcolor: "rgba(248,250,252,0.92)",
                              fontWeight: 800,
                              color: "text.secondary",
                              borderBottom: "1px solid rgba(15,23,42,0.06)",
                              fontSize: "0.8rem",
                              textTransform: "uppercase",
                              letterSpacing: "0.03em",
                            },
                          }}
                        >
                          <TableCell>Serviço</TableCell>
                          <TableCell>Data</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Observações</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {recent.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={4}
                              align="center"
                              sx={{ py: 6, color: "text.secondary" }}
                            >
                              Ainda não tens reservas.
                            </TableCell>
                          </TableRow>
                        ) : (
                          recent.map((r) => (
                            <TableRow
                              key={r.id}
                              hover
                              sx={{
                                "& td": {
                                  borderBottom:
                                    "1px solid rgba(15,23,42,0.05)",
                                },
                                "&:last-child td": { borderBottom: 0 },
                              }}
                            >
                              <TableCell sx={{ fontWeight: 700 }}>
                                {r.serviceTitle || "(serviço)"}
                              </TableCell>
                              <TableCell>
                                {r.createdAt?.toDate
                                  ? r.createdAt.toDate().toLocaleDateString()
                                  : "—"}
                              </TableCell>
                              <TableCell>{getStatusChip(r.status)}</TableCell>
                              <TableCell
                                sx={{
                                  maxWidth: 360,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  color: "text.secondary",
                                }}
                              >
                                {r.acompanhamento || "—"}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>

              <Grid item xs={12} xl={4}>
                <Stack spacing={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "28px",
                      color: "common.white",
                      background:
                        "linear-gradient(135deg, #111827 0%, #312e81 100%)",
                      boxShadow: "0 24px 60px rgba(49,46,129,0.24)",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: -40,
                        right: -30,
                        width: 140,
                        height: 140,
                        borderRadius: "50%",
                        background: "rgba(255,255,255,0.08)",
                        filter: "blur(10px)",
                      }}
                    />

                    <Box sx={{ position: "relative", zIndex: 1 }}>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 800,
                          opacity: 0.72,
                          textTransform: "uppercase",
                          letterSpacing: ".14em",
                        }}
                      >
                        Assistência rápida
                      </Typography>

                      <Typography variant="h5" sx={{ mt: 1.5, fontWeight: 900 }}>
                        Precisas de assistência?
                      </Typography>

                      <Typography
                        sx={{ mt: 1.2, color: "rgba(255,255,255,0.76)" }}
                      >
                        Marca já uma nova reserva de serviço e acompanha tudo
                        numa única área.
                      </Typography>

                      <Button
                        variant="contained"
                        component={RouterLink}
                        to="/servicos"
                        endIcon={<ArrowForwardRoundedIcon />}
                        sx={{
                          mt: 3,
                          px: 2.5,
                          py: 1.2,
                          borderRadius: "16px",
                          textTransform: "none",
                          fontWeight: 800,
                          bgcolor: "common.white",
                          color: "#111827",
                          "&:hover": { bgcolor: "grey.100" },
                        }}
                      >
                        Ver serviços
                      </Button>
                    </Box>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "28px",
                      border: "1px solid rgba(15,23,42,0.06)",
                      background: "rgba(255,255,255,0.78)",
                      backdropFilter: "blur(12px)",
                      boxShadow: "0 24px 60px rgba(15,23,42,0.06)",
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 2 }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 900 }}>
                        Resumo rápido
                      </Typography>
                      <TrendingUpRoundedIcon sx={{ color: "#2563eb" }} />
                    </Stack>

                    <Stack spacing={1.4}>
                      <Box
                        sx={{
                          p: 1.8,
                          borderRadius: "18px",
                          bgcolor: "rgba(99,102,241,0.06)",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Total de reservas
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "1.5rem",
                            fontWeight: 900,
                            letterSpacing: "-0.03em",
                          }}
                        >
                          {stats.total}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          p: 1.8,
                          borderRadius: "18px",
                          bgcolor: "rgba(16,185,129,0.08)",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Serviços concluídos
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "1.5rem",
                            fontWeight: 900,
                            letterSpacing: "-0.03em",
                          }}
                        >
                          {stats.concluidos}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          p: 1.8,
                          borderRadius: "18px",
                          bgcolor: "rgba(245,158,11,0.10)",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Pedidos em acompanhamento
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "1.5rem",
                            fontWeight: 900,
                            letterSpacing: "-0.03em",
                          }}
                        >
                          {stats.emAnalise + stats.emReparacao}
                        </Typography>
                      </Box>
                    </Stack>

                    <Divider sx={{ my: 2.25 }} />

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.7 }}
                    >
                      Mantém o acompanhamento das tuas reservas e volta aqui
                      para veres rapidamente o estado dos teus serviços.
                    </Typography>
                  </Paper>
                </Stack>
              </Grid>
            </Grid>
          )}
        </Box>
      </Box>
    </PageLayout>
  );
}